import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Json } from "@how/database";
import { churchToday, richTextSchema, type RichText } from "@how/website-core";
import { useTenantRecord } from "../tenant/queries";

/*
 * Devotionals (ADR 0077): the church's daily devotionals. Staff with devotionals.read read the table directly (the
 * policies decide); staff with devotionals.manage write through the definer functions of
 * 20261030101000_devotionals.sql, which check devotionals.manage and the devotionals module again.
 */

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

// --------------------------------------------------------------------------------------------------- rows

export interface DevotionalRow {
  readonly id: string;
  readonly title: string;
  readonly slug: string;
  /** The day it is for, YYYY-MM-DD, in the church's calendar. */
  readonly date: string;
  readonly seriesTitle: string | null;
  readonly seriesSlug: string | null;
  readonly scriptureReference: string | null;
  readonly scriptureText: string | null;
  readonly scriptureVersion: string | null;
  readonly reflection: RichText | null;
  readonly prayer: string | null;
  readonly imagePath: string | null;
  readonly imageAlt: string | null;
  readonly authorName: string | null;
  readonly authorPersonId: string | null;
  readonly isPublished: boolean;
  readonly publishedAt: string | null;
}

// --------------------------------------------------------------------------------------------------- reading

export function useDevotionals() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["devotionals", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<DevotionalRow[]> => {
      const { data, error } = await supabase
        .from("devotionals")
        .select(
          "id, title, slug, devotional_date, series_title, series_slug, scripture_reference, scripture_text, scripture_version, reflection, prayer, image_path, image_alt, author_name, author_person_id, is_published, published_at",
        )
        // RLS admits every church the reader belongs to; the screen shows one church.
        .eq("tenant_id", tenantId ?? "")
        .order("devotional_date", { ascending: false })
        .limit(1000);
      if (error) throw error;
      return data.map((row) => {
        const reflection = richTextSchema.safeParse(row.reflection);
        return {
          id: row.id,
          title: row.title,
          slug: row.slug,
          date: row.devotional_date,
          seriesTitle: row.series_title,
          seriesSlug: row.series_slug,
          scriptureReference: row.scripture_reference,
          scriptureText: row.scripture_text,
          scriptureVersion: row.scripture_version,
          reflection: reflection.success && reflection.data.length > 0 ? reflection.data : null,
          prayer: row.prayer,
          imagePath: row.image_path,
          imageAlt: row.image_alt,
          authorName: row.author_name,
          authorPersonId: row.author_person_id,
          isPublished: row.is_published,
          publishedAt: row.published_at,
        };
      });
    },
  });
}

/** The browser's own calendar date, YYYY-MM-DD: the fallback when the church's time zone is not known. */
export function browserToday(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Today's date in the church's time zone, which is when a published devotional's day begins on the website and in the
 * portal. Until the church's record is read, or when it has no zone, the browser's own date stands in.
 */
export function useChurchToday(): string {
  const record = useTenantRecord();
  const zone = record.data?.time_zone;
  return typeof zone === "string" && zone !== "" ? churchToday(Date.now(), zone) : browserToday();
}

// --------------------------------------------------------------------------------------------------- checking

/** What a devotional's form holds before it is saved. */
export interface DevotionalDraft {
  readonly title: string;
  /** YYYY-MM-DD. */
  readonly date: string;
  readonly seriesTitle: string;
  readonly scriptureReference: string;
  readonly scriptureText: string;
  readonly scriptureVersion: string;
  readonly reflection: RichText | null;
  readonly prayer: string;
  readonly imagePath: string | null;
  readonly imageAlt: string | null;
  readonly authorName: string;
  /** The person the author is, when the devotional was linked to one; kept as it is. */
  readonly authorPersonId: string | null;
}

/** The problems with a devotional's form, by field, in the words the form uses; empty when it can be saved. */
export function devotionalProblems(draft: DevotionalDraft): Partial<Record<string, string>> {
  const problems: Record<string, string> = {};
  const title = draft.title.trim();
  if (title === "") problems["title"] = "Give the devotional a title";
  else if (title.length > 160) problems["title"] = "At most 160 characters";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.date)) problems["date"] = "Choose the day it is for";
  if (draft.seriesTitle.trim().length > 120) problems["seriesTitle"] = "At most 120 characters";
  if (draft.scriptureReference.trim().length > 120)
    problems["scriptureReference"] = "At most 120 characters";
  if (draft.scriptureText.length > 6000) problems["scriptureText"] = "At most 6,000 characters";
  if (draft.scriptureVersion.trim().length > 40)
    problems["scriptureVersion"] = "At most 40 characters";
  if (draft.prayer.length > 4000) problems["prayer"] = "At most 4,000 characters";
  if (draft.authorName.trim().length > 120) problems["authorName"] = "At most 120 characters";
  return problems;
}

/** The JSON save_devotional reads. Empty text is sent as "", which the database stores as nothing. */
export function fieldsOf(draft: DevotionalDraft): Record<string, Json> {
  return {
    title: draft.title.trim(),
    date: draft.date,
    seriesTitle: draft.seriesTitle.trim(),
    scriptureReference: draft.scriptureReference.trim(),
    scriptureText: draft.scriptureText.trim(),
    scriptureVersion: draft.scriptureVersion.trim(),
    reflection: draft.reflection ?? [],
    prayer: draft.prayer.trim(),
    imagePath: draft.imagePath ?? "",
    imageAlt: draft.imagePath === null ? "" : (draft.imageAlt ?? ""),
    authorName: draft.authorName.trim(),
    authorPersonId: draft.authorPersonId ?? "",
  };
}

// --------------------------------------------------------------------------------------------------- writing

function useDevotionalInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: ["devotionals", tenantId] });
  };
}

export function useSaveDevotional() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useDevotionalInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly devotionalId: string | null;
      readonly draft: DevotionalDraft;
    }): Promise<string> => {
      if (tenantId === null) throw new Error("no church resolved");
      const { data, error } = await supabase.rpc("save_devotional", {
        p_tenant_id: tenantId,
        p_fields: fieldsOf(input.draft),
        ...(input.devotionalId === null ? {} : { p_devotional_id: input.devotionalId }),
      });
      failed(error);
      return data as string;
    },
    onSuccess: invalidate,
  });
}

export function usePublishDevotional() {
  const supabase = useSupabase();
  const invalidate = useDevotionalInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly devotionalId: string; readonly published: boolean }) => {
      const { error } = await supabase.rpc("publish_devotional", {
        p_devotional_id: input.devotionalId,
        p_published: input.published,
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

export function useDeleteDevotional() {
  const supabase = useSupabase();
  const invalidate = useDevotionalInvalidation();
  return useMutation({
    mutationFn: async (devotionalId: string) => {
      const { error } = await supabase.rpc("delete_devotional", { p_devotional_id: devotionalId });
      failed(error);
    },
    onSuccess: invalidate,
  });
}
